import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FeatherIcon } from '../../components/FeatherIcon'
import { LoadingBlock } from '../../components/Spinner'
import { useFanslyApi } from '../deps'
import { toFanslyError } from '../useFanslyQuery'
import { gifCategoriesOf, gifsOf } from './format'
import type { ChatGif } from './model'

/**
 * The GIF panel over the composer: Fansly's featured GIFs and categories,
 * search as you type. A pick is sent as Fansly does it (the GIF's share URL,
 * which Fansly turns into an embed).
 */
export function GifPicker({ onPick, onClose }: { onPick: (gif: ChatGif) => void; onClose: () => void }) {
  const { t } = useTranslation('fanslyChats')
  const api = useFanslyApi()
  const [query, setQuery] = useState('')
  const [items, setItems] = useState<ChatGif[]>([])
  const [categories, setCategories] = useState<string[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const field = useRef<HTMLInputElement>(null)
  const seq = useRef(0)

  useEffect(() => {
    field.current?.focus()
    api
      .get('/crm/gifs/categories')
      .then((p) => setCategories(gifCategoriesOf(p)))
      .catch(() => undefined)
  }, [api])

  useEffect(() => {
    const q = query.trim()
    const mine = ++seq.current
    const timer = window.setTimeout(
      () => {
        setLoading(true)
        setError('')
        ;(q ? api.get('/crm/gifs/search', { search: q }) : api.get('/crm/gifs/featured'))
          .then((p) => mine === seq.current && setItems(gifsOf(p)))
          .catch((e: unknown) => mine === seq.current && setError(toFanslyError(e).message))
          .finally(() => mine === seq.current && setLoading(false))
      },
      q ? 450 : 0,
    )
    return () => window.clearTimeout(timer)
  }, [api, query])

  return (
    <div className="rc-pop rc-gif-pop fsc-gif-pop" role="dialog" aria-label={t('gif.title')}>
      <div className="rc-pop-head">
        <FeatherIcon name="search" size={14} />
        <input
          ref={field}
          value={query}
          placeholder={t('gif.search')}
          aria-label={t('gif.search')}
          autoComplete="off"
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => e.key === 'Escape' && onClose()}
        />
        <button type="button" className="rc-pop-close" title={t('close')} aria-label={t('close')} onClick={onClose}>
          <FeatherIcon name="x" size={14} />
        </button>
      </div>
      {!query && categories.length > 0 && (
        <div className="fsc-gif-cats">
          {categories.map((c) => (
            <button key={c} type="button" className="lib-chip" onClick={() => setQuery(c)}>
              {c}
            </button>
          ))}
        </div>
      )}
      <div className="rc-gif-grid">
        {!loading &&
          items.map((g) => (
            <button key={g.url} type="button" className="rc-gif-item" title={g.title || t('gif.one')} onClick={() => onPick(g)}>
              <img src={g.src} alt={g.title || t('gif.one')} loading="lazy" />
            </button>
          ))}
        {loading && <LoadingBlock pad="16px 0" label={t('gif.loading')} style={{ gridColumn: '1 / -1' }} />}
        {!loading && error && <div className="rc-pop-empty">{error}</div>}
        {!loading && !error && !items.length && <div className="rc-pop-empty">{t('gif.empty')}</div>}
      </div>
    </div>
  )
}
